import React from 'react'
import { useTranslation } from 'react-i18next'
import type { PortalMembership } from '../../lib/portal-membership'

/**
 * FF-3636. Two variants:
 *
 * - `redirecting`: the user is being sent to their own portal right now. Shown only for the moment
 *   the browser takes to follow `window.location.replace`, so it explains the redirect instead of
 *   (falsely) claiming the environment is misconfigured.
 * - default: the terminal fallback when a wrong-portal user cannot be redirected — no matching
 *   portal, or the destination URL is unset. They are already signed out; the screen says why.
 *
 * Portal-specific copy lives in each app's translation.json under `wrongPortal.*`.
 */
export function WrongPortalNotice({
  membership,
  redirecting = false,
}: {
  membership: PortalMembership
  redirecting?: boolean
}) {
  const { t } = useTranslation()
  const home = membership === 'unknown' ? '' : t(`wrongPortal.portals.${membership}`)

  if (redirecting && home) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="max-w-md text-center space-y-4 p-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('wrongPortal.redirectingTitle')}</h1>
          <p className="text-gray-600">{t('wrongPortal.redirectingBody', { portal: home })}</p>
          <p className="text-gray-600">{t('wrongPortal.signedOut')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="max-w-md text-center space-y-4 p-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('wrongPortal.title')}</h1>
        <p className="text-gray-600">
          {home ? t('wrongPortal.bodyWithHome', { portal: home }) : t('wrongPortal.bodyUnknown')}
        </p>
        <p className="text-gray-600">{t('wrongPortal.signedOut')}</p>
        <a
          href="#/auth/login"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-800 hover:bg-gray-700"
        >
          {t('wrongPortal.backToSignIn')}
        </a>
      </div>
    </div>
  )
}
